import { useMemo } from "react";
import { usePermissions } from "./usePermissions";
import { PermissionsMap } from "../../domain/entities/PermissionEntity";
import { MODULE_CODES, ACTION_CODES } from "../../constants/permissions";

export interface BankTransactionPermissionsState {
  isInitialLoading: boolean;
  permissions: PermissionsMap | null;

  // Permission flags
  canCreate: boolean;
  canAdvance: boolean;
  canMatch: boolean;
  canSplitTransaction: boolean;
  canUnmatch: boolean;
  canProcessHighValueExpense: boolean;

  // Review requirements
  needsReviewForCreate: boolean;
  needsReviewForAdvance: boolean;
  needsReviewForMatch: boolean;
  needsReviewForSplitTransaction: boolean;
  needsReviewForUnmatch: boolean;
  needsReviewForHighValueExpense: boolean;

  error: Error | null;
}

/**
 * Bank-transaction-specific permission flags derived from the global PermissionsContext.
 * Uses the same permissions fetched once by PermissionsProvider (no duplicate API call).
 * isInitialLoading is true while the context is still loading permissions.
 */
export const useBankTransactionPermissions =
  (): BankTransactionPermissionsState => {
    const { permissions, loading, error, getPermissionInfo } = usePermissions();

    const derived = useMemo(() => {
      const createInfo = getPermissionInfo(
        MODULE_CODES.BANK_TRANSACTION,
        ACTION_CODES.CREATE,
      );
      const advanceInfo = getPermissionInfo(
        MODULE_CODES.BANK_TRANSACTION,
        ACTION_CODES.ADVANCE,
      );
      const matchInfo = getPermissionInfo(
        MODULE_CODES.BANK_TRANSACTION,
        ACTION_CODES.MATCH,
      );
      const splitInfo = getPermissionInfo(
        MODULE_CODES.BANK_TRANSACTION,
        ACTION_CODES.SPLIT_TRANSACTION,
      );
      const unmatchInfo = getPermissionInfo(
        MODULE_CODES.BANK_TRANSACTION,
        ACTION_CODES.UNMATCH,
      );
      const expenseHighValueInfo = getPermissionInfo(
        MODULE_CODES.BANK_TRANSACTION,
        ACTION_CODES.EXPENSE_HIGH_VALUE,
      );

      return {
        canCreate: createInfo.granted,
        canAdvance: advanceInfo.granted,
        canMatch: matchInfo.granted,
        canSplitTransaction: splitInfo.granted,
        canUnmatch: unmatchInfo.granted,
        canProcessHighValueExpense: expenseHighValueInfo.granted,
        needsReviewForCreate: createInfo.requiresReview,
        needsReviewForAdvance: advanceInfo.requiresReview,
        needsReviewForMatch: matchInfo.requiresReview,
        needsReviewForSplitTransaction: splitInfo.requiresReview,
        needsReviewForUnmatch: unmatchInfo.requiresReview,
        needsReviewForHighValueExpense: expenseHighValueInfo.requiresReview,
      };
    }, [getPermissionInfo]);

    return {
      isInitialLoading: loading,
      permissions,
      ...derived,
      error,
    };
  };
